import * as z from "zod/v4";
import { ReturnTypeEnum } from "../../return";
import { CompensationMethod } from "./filters/compensation-method-filter";
import { ViewByEnum } from "./filters/view-by-toggle";

/**
 * Schema for return analytics series data
 * This schema defines the data structure for time-series analytics
 */

// Input schema for return analytics series data
export const ReturnAnalyticsSeriesDataInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
  types: z
    .array(z.enum(ReturnTypeEnum))
    .describe("Types of returns to include in the analytics"),
  salesChannels: z
    .array(z.string())
    .optional()
    .describe("Sales channels to filter by (optional)"),
  returnStatuses: z
    .array(z.string())
    .optional()
    .describe(
      "Return statuses to filter by. If omitted, all statuses will be included.",
    ),
  compensationMethods: z
    .array(z.enum(CompensationMethod))
    .optional()
    .describe("Compensation methods to filter by (optional)"),
  viewBy: z
    .enum([
      ViewByEnum.ORDER_DATE,
      ViewByEnum.PROCESSED_DATE,
      ViewByEnum.SUBMITTED_DATE,
    ])
    .default(ViewByEnum.ORDER_DATE)
    .describe(
      "Whether to view analytics by order date, submitted date, or processed date",
    ),
});

// Data point schema for time-series data
export const ReturnAnalyticsDataPointSchema = z.object({
  date: z.string().describe("Date for this data point (YYYY-MM-DD)"),
  returnedOrdersRate: z
    .number()
    .describe("Percentage of orders that had returns on this date"),
  returnedItemsRate: z
    .number()
    .describe("Percentage of items that were returned on this date"),
  returnedRevenueRate: z
    .number()
    .describe("Percentage of revenue affected by returns on this date"),
  protectedOrdersRateTotal: z
    .number()
    .describe("Percentage of protected orders out of total orders"),
  protectedOrdersRateEligible: z
    .number()
    .describe("Percentage of protected orders out of eligible orders"),
});

// Output schema for return analytics series data
export const ReturnAnalyticsSeriesDataOutputSchema = z.object({
  data: z
    .array(ReturnAnalyticsDataPointSchema)
    .describe(
      "Array of data points for day by day breakdown of return/claims analytics",
    ),
});

// Export type definitions for TypeScript
export type ReturnAnalyticsSeriesDataInput = z.infer<
  typeof ReturnAnalyticsSeriesDataInputSchema
>;
export type ReturnAnalyticsSeriesDataOutput = z.infer<
  typeof ReturnAnalyticsSeriesDataOutputSchema
>;
export type ReturnAnalyticsDataPoint = z.infer<
  typeof ReturnAnalyticsDataPointSchema
>;

// Define the entire schema for RPC usage
export const returnAnalyticsSeriesData = {
  name: "returnAnalyticsSeriesData" as const,
  input: ReturnAnalyticsSeriesDataInputSchema,
  output: ReturnAnalyticsSeriesDataOutputSchema,
};
